import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
import {
  ContentFragmentInfoLayer,
  RawHTMLContent,
} from '@sparknz/journeys-common';
import {
  bodyMDemiStyle,
  bodySDemiStyle,
  bodySMediumStyle,
  Box,
  Grid,
  GridColumn,
  GridContainer,
  GridRow,
  InformationLayer,
  spacing as spacingToken,
  subheadingMStyle,
} from '@sparknz/spark-pattern-library';
import { useTrackPageView } from '@sparknz/tracking';

import { ProductDetailsQuery$data } from '__generated__/ProductDetailsQuery.graphql';
import { RichTextContentAccordion } from 'shared/components';
import { useQuery, useSegment } from 'shared/hooks';
import { useAuthorableContents } from 'shared/providers/AuthorableContentsProvider';
import { useIsWebView } from 'shared/providers/isWebView';
import { extractFirstValue } from 'shared/utils';
import { useProductDetails } from '../../provider';
import { ProductDetailsActionTypes } from '../../reducer/productDetails.reducer';
import { InfoLayerStatus, PrimaryCategory } from '../../typings';
import { trackLinkedProductsEvent } from '../../utils';
import {
  getDeviceOfferId,
  getSelectedDeviceById,
} from '../../utils/device.utils';
import Breadcrumbs from '../BreadCrumbs';
import FAQAccordion from '../FAQAccordion';
import TechSpecsAccordion from '../TechSpecsAccordion';
import {
  Configurator,
  ImageGallery,
  PreOrderBanner,
  ProductConfiguratorLoader,
} from './components';
import TradeInActionLayer from './components/TradeInActionLayer';
import { FEATURES_ID } from './constants';

const CONTENT_TYPOGRAPHY = css`
  h6 {
    ${subheadingMStyle}
  }

  p {
    ${bodySMediumStyle}
  }

  b {
    ${bodyMDemiStyle}
  }

  ol,
  ul {
    padding-left: ${spacingToken.s2};

    li {
      ${bodySDemiStyle}
    }
  }
`;

const OverflowHiddenWrapper = styled(Box)`
  overflow-x: hidden;
`;

const ImageGalleryAndConfiguratorGridRow = styled(GridRow)`
  ${({ theme: { media } }) => css`
    ${media.up('slim')} {
      flex-direction: row-reverse;
    }
  `}
`;

interface ProductConfiguratorProps {
  product: ProductDetailsQuery$data;
  subCategoryId: string;
}

export default function ProductConfigurator({
  product,
  subCategoryId,
}: ProductConfiguratorProps) {
  const { query, replaceQuery } = useQuery();
  const { planId } = query;
  const router = useRouter();
  const { isBusiness } = useSegment();

  const {
    dispatch,
    selectedDevice,
    planOffers: planOfferDetails,
    ifpEligibility: { isChangePlan },
    changePlanOffers,
    paymentOptions,
    deviceOffers,
  } = useProductDetails();

  const planOffers = changePlanOffers || planOfferDetails;

  const ifpId = extractFirstValue(router.query.ifpId);

  // Sends tracking event whenever the selectedDevice changes
  useEffect(() => {
    if (selectedDevice) {
      trackLinkedProductsEvent({
        eventInfo: {
          eventName: 'Product View',
          eventAction: 'shop.prodViewAction',
        },
        device: selectedDevice,
        isBusiness,
        paymentOption: paymentOptions?.find(({ id }) => id === ifpId),
      });
    }
  }, [selectedDevice, isBusiness]);

  useEffect(() => {
    if (selectedDevice) {
      trackLinkedProductsEvent({
        eventInfo: {
          eventName: 'Select Mobile Plan',
          eventAction: 'shop.selectMobilePlan',
        },
        device: selectedDevice,
        plan: planOffers?.find((plan) => plan.id === planId),
        isBusiness,
        isChangePlan,
      });
    }
  }, [planId]);

  useEffect(() => {
    if (!router.isReady) return;

    const offerId = getDeviceOfferId(query.offerId);
    if (offerId !== selectedDevice.id) {
      const { id } = getSelectedDeviceById(deviceOffers, offerId);
      dispatch({
        type: ProductDetailsActionTypes.SelectDevice,
        selectedDeviceId: id,
      });
      replaceQuery({ ...query, offerId: id });
    }
  }, [query]);

  if (!selectedDevice) {
    return <ProductConfiguratorLoader />;
  }

  return (
    <ProductConfiguratorInternal
      product={product}
      subCategoryId={subCategoryId}
    />
  );
}

function ProductConfiguratorInternal({
  subCategoryId,
}: ProductConfiguratorProps) {
  const isWebView = useIsWebView();
  const { isBusiness, isLargeBusiness } = useSegment();
  const { dispatch, infoLayer, selectedDevice } = useProductDetails();
  const { description, primaryCategory, groupName, richTextContent } =
    selectedDevice;
  const { getText } = useAuthorableContents();
  const isMobileDeviceSelected =
    selectedDevice.primaryCategory.id === PrimaryCategory.HANDSET;

  useTrackPageView({
    pageName: `shop.productInfoState: ${groupName}`,
    pageType: 'Product Page',
    breadcrumbs: ['home', 'shop', primaryCategory?.name, groupName],
  });

  const updateInfoLayer = (
    status = InfoLayerStatus.Close,
    contentKey?: string,
    isNested?: boolean,
  ) => {
    dispatch({
      type: ProductDetailsActionTypes.SetInfoLayer,
      infoLayer: {
        status,
        contentKey,
        isNested,
      },
    });
  };

  const showBreadcrumbs = !isWebView && primaryCategory && groupName;

  return (
    <>
      <ContentFragmentInfoLayer
        isOpen={infoLayer.status === InfoLayerStatus.ContentFragment}
        isNested={infoLayer.isNested}
        onClose={() => updateInfoLayer()}
        contentFragmentKey={infoLayer.contentKey}
        segment={isBusiness ? 'business' : 'consumer'}
      />

      <InformationLayer
        isOpen={infoLayer.status === InfoLayerStatus.RawHtml}
        onClose={() => updateInfoLayer()}
      >
        <RawHTMLContent
          content={getText(infoLayer.contentKey)}
          typography={CONTENT_TYPOGRAPHY}
        />
      </InformationLayer>
      {isMobileDeviceSelected ? <TradeInActionLayer /> : null}

      <Box py={['s5', 's7', 's9']}>
        <GridContainer variant="full-width">
          <Grid>
            <GridRow>
              <GridColumn lil={2} slim={6} big={12}>
                <OverflowHiddenWrapper px="s1">
                  {showBreadcrumbs ? (
                    <Breadcrumbs
                      category={primaryCategory}
                      deviceGroupName={groupName}
                      subCategoryId={subCategoryId}
                    />
                  ) : null}
                </OverflowHiddenWrapper>
              </GridColumn>
            </GridRow>

            <ImageGalleryAndConfiguratorGridRow>
              <ImageGallery />

              <GridColumn lil={2} slim={3} big={6}>
                <OverflowHiddenWrapper px="s1">
                  <PreOrderBanner
                    preOrderable={selectedDevice.preOrderable}
                    releaseDate={selectedDevice.releaseDate}
                    onClickLink={() =>
                      updateInfoLayer(
                        InfoLayerStatus.RawHtml,
                        'productDetails.preorder.terms',
                        false,
                      )
                    }
                  />
                  <Configurator />
                </OverflowHiddenWrapper>
              </GridColumn>
            </ImageGalleryAndConfiguratorGridRow>

            <GridRow id={FEATURES_ID}>
              <GridColumn lil={2} slim={6} big={8} bigOffset={2}>
                <Box pt={['s5', 's7', 's10']} pb={['s7', 's9', 's9']}>
                  <RichTextContentAccordion
                    richTextContent={richTextContent}
                    title="Additional info"
                    description={description}
                  />
                </Box>
              </GridColumn>
            </GridRow>

            <GridRow>
              <GridColumn lil={2} slim={6} big={8} bigOffset={2}>
                <TechSpecsAccordion />
              </GridColumn>
            </GridRow>

            {isLargeBusiness ? null : (
              <GridRow>
                <GridColumn lil={2} slim={6} big={8} bigOffset={2}>
                  <FAQAccordion />
                </GridColumn>
              </GridRow>
            )}
          </Grid>
        </GridContainer>
      </Box>
    </>
  );
}
